import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ReloadOutlined,
  SearchOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  InfoCircleOutlined,
  SwapOutlined,
  RightOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import SideModal from '../../components/shared/SideModal/SideModal';
import {
  MARKETPLACE_CATEGORIES,
  MARKETPLACE_PRODUCTS,
  MarketplaceService,
  MarketplaceProduct,
  getCategoryLabel,
  getProductByKey,
  getProductsByCategory,
} from './marketplaceCatalog';
import { MarketplaceProductLogo } from './components/MarketplaceProductLogo';

// ─── Status config ───────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  running: {
    label: 'Running',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: <CheckCircleOutlined />,
  },
  stopped: {
    label: 'Stopped',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    icon: <PauseCircleOutlined />,
  },
  deploying: {
    label: 'Deploying',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: <ClockCircleOutlined />,
  },
  error: {
    label: 'Error',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    icon: <ExclamationCircleOutlined />,
  },
};

// ─── Main Component ──────────────────────────────────────
const MarketplacePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { isMarketplaceProductVisible } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();

  // Solo las apps que el admin dejó visibles (missing = visible).
  const visibleProducts = useMemo(
    () => MARKETPLACE_PRODUCTS.filter((p) => isMarketplaceProductVisible(p.key)),
    [isMarketplaceProductVisible]
  );

  const productParam = searchParams.get('product') || '';
  const selectedProductKey = useMemo(() => {
    if (!productParam) return '';
    return getProductByKey(productParam) ? productParam : '';
  }, [productParam]);

  const isBrowseMode = !selectedProductKey;

  const [servicesByProduct, setServicesByProduct] = useState<Record<string, MarketplaceService[]>>({});
  const [loadingByProduct, setLoadingByProduct] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogLayout, setCatalogLayout] = useState<'grid' | 'list'>('grid');
  const [showHelp, setShowHelp] = useState(false);

  const selectedProduct = useMemo(
    () => (selectedProductKey ? getProductByKey(selectedProductKey) : undefined),
    [selectedProductKey]
  );

  const filteredCatalogProducts = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return visibleProducts;
    return visibleProducts.filter((p) => {
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.subtitle.toLowerCase().includes(q) ||
        getCategoryLabel(p.categoryKey).toLowerCase().includes(q)
      );
    });
  }, [catalogSearch, visibleProducts]);

  // ─── Fetch all products ────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!accessToken) return;

    const loadingState: Record<string, boolean> = {};
    visibleProducts.forEach((p) => { loadingState[p.key] = true; });
    setLoadingByProduct(loadingState);

    const results = await Promise.allSettled(
      visibleProducts.map(async (product) => {
        const data = await servicesService.getByCategory(product.apiCategory);
        const items = data?.services || (Array.isArray(data) ? data : []);
        return { key: product.key, services: items.map(product.mapService) };
      })
    );

    const newServices: Record<string, MarketplaceService[]> = {};
    const newLoading: Record<string, boolean> = {};

    results.forEach((result, idx) => {
      const productKey = visibleProducts[idx].key;
      newLoading[productKey] = false;
      if (result.status === 'fulfilled') {
        newServices[result.value.key] = result.value.services;
      } else {
        newServices[productKey] = [];
      }
    });

    setServicesByProduct(newServices);
    setLoadingByProduct(newLoading);
  }, [accessToken, visibleProducts]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Select product / browse ─────────────────────────────
  const handleSelectBrowse = () => {
    setSearchParams({}, { replace: true });
    setSearchTerm('');
    setFilterStatus('all');
  };

  const handleSelectProduct = (key: string) => {
    setSearchParams({ product: key }, { replace: true });
    setSearchTerm('');
    setFilterStatus('all');
  };

  // ─── Filtered services for selected product ────────────
  const filteredServices = useMemo(() => {
    const all = servicesByProduct[selectedProductKey] || [];
    return all.filter((svc) => {
      const matchesSearch =
        !searchTerm ||
        svc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        svc.instanceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        svc.region.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || svc.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [servicesByProduct, selectedProductKey, searchTerm, filterStatus]);

  const isLoading = selectedProductKey ? !!loadingByProduct[selectedProductKey] : false;
  const isAnyLoading = Object.values(loadingByProduct).some(Boolean);

  // ─── Stats for selected product ────────────────────────
  const allServices = selectedProductKey ? servicesByProduct[selectedProductKey] || [] : [];
  const runningCount = allServices.filter((s) => s.status === 'running').length;
  const stoppedCount = allServices.filter((s) => s.status === 'stopped').length;
  const deployingCount = allServices.filter((s) => s.status === 'deploying').length;

  // ─── Group sidebar items by category ───────────────────
  const groupedProducts = useMemo(() => {
    const groups: { category: string; label: string; products: MarketplaceProduct[] }[] = [];
    MARKETPLACE_CATEGORIES.forEach((cat) => {
      const prods = visibleProducts.filter((p) => p.categoryKey === cat.key);
      if (prods.length > 0) {
        groups.push({ category: cat.key, label: cat.label, products: prods });
      }
    });
    return groups;
  }, [visibleProducts]);

  return (
    <div>
      {/* ─── Fixed Sidebar (full height, below navbar) ─── */}
      <aside className="fixed top-16 left-0 bottom-0 w-56 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 hidden lg:flex flex-col z-30 overflow-y-auto">
        {/* Sidebar header */}
        <div className="px-5 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('marketplace.title') || 'Marketplace'}
          </h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {visibleProducts.length} {t('marketplace.products') || 'products'}
          </p>
        </div>

        {/* Browse all */}
        <div className="px-3 pt-2 pb-1">
          <button
            type="button"
            onClick={handleSelectBrowse}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-all ${
              isBrowseMode
                ? 'bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border border-transparent'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isBrowseMode ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700'
              }`}
            >
              <AppstoreOutlined
                className={`w-4 h-4 ${isBrowseMode ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}
              />
            </div>
            <span
              className={`text-sm font-medium truncate ${
                isBrowseMode ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {t('marketplace.allProducts') || 'All products'}
            </span>
          </button>
        </div>

        {/* Product list grouped by category */}
        <div className="flex-1 py-2">
          {groupedProducts.map((group) => (
            <div key={group.category} className="mb-1">
              {/* Category label */}
              <div className="px-5 py-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {group.label}
                </span>
              </div>

              {/* Products in category */}
              {group.products.map((product) => {
                const count = servicesByProduct[product.key]?.length || 0;
                const isSelected = product.key === selectedProductKey;

                return (
                  <button
                    key={product.key}
                    onClick={() => handleSelectProduct(product.key)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-all ${
                      isSelected
                        ? 'bg-gray-100 dark:bg-gray-700/60 border-r-2 border-gray-900 dark:border-white'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-r-2 border-transparent'
                    }`}
                  >
                    <MarketplaceProductLogo product={product} variant="sidebar" isSelected={isSelected} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isSelected
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {product.name}
                      </p>
                    </div>
                    {count > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        isSelected
                          ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      {/* ─── Main Content (offset by sidebar width on lg) ─── */}
      <div className="lg:pl-56">
        <div className="max-w-[1400px] mx-auto px-8 py-6">

          {/* ─── Page Title ──────────────────────────────── */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {t('marketplace.title') || 'Marketplace'}
                  </h1>
                  <button
                    onClick={() => setShowHelp(true)}
                    className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                  >
                    <InfoCircleOutlined className="text-lg" />
                  </button>
                </div>
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('marketplace.subtitle') || 'Ready-to-deploy services'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
                  {t('marketplace.description') || 'Browse and deploy pre-configured services like message brokers, automation tools, and content management systems.'}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a
                  href="https://docs.kubiy.com/marketplace"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <InfoCircleOutlined />
                  Documentación
                </a>
                <button
                  onClick={fetchAll}
                  disabled={isAnyLoading}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ReloadOutlined className={isAnyLoading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile product selector (visible on small screens) */}
          <div className="lg:hidden mb-4">
            <select
              value={selectedProductKey}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) handleSelectBrowse();
                else handleSelectProduct(v);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">{t('marketplace.allProducts') || 'All products'}</option>
              {visibleProducts.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name} — {getCategoryLabel(p.categoryKey)}
                </option>
              ))}
            </select>
          </div>

          {isBrowseMode ? (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6 items-stretch sm:items-center justify-between">
                <div className="flex-1 relative max-w-xl">
                  <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder={t('marketplace.searchProducts') || 'Search products by name...'}
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCatalogLayout('grid')}
                    className={`px-3 py-2 text-sm flex items-center gap-1.5 ${
                      catalogLayout === 'grid'
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                    aria-pressed={catalogLayout === 'grid'}
                  >
                    <AppstoreOutlined />
                    {t('marketplace.layoutGrid') || 'Grid'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogLayout('list')}
                    className={`px-3 py-2 text-sm flex items-center gap-1.5 border-l border-gray-200 dark:border-gray-700 ${
                      catalogLayout === 'list'
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                    aria-pressed={catalogLayout === 'list'}
                  >
                    <UnorderedListOutlined />
                    {t('marketplace.layoutList') || 'List'}
                  </button>
                </div>
              </div>

              {filteredCatalogProducts.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-dashed">
                  <SearchOutlined className="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {t('marketplace.noProductsMatch') || 'No products match your search'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('marketplace.tryOtherTerms') || 'Try different keywords or clear the filter.'}
                  </p>
                </div>
              ) : catalogLayout === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredCatalogProducts.map((p) => {
                    return (
                      <div
                        key={p.key}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(p.createRoute)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(p.createRoute);
                          }
                        }}
                        className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all cursor-pointer text-left"
                      >
                        <MarketplaceProductLogo product={p} variant="catalogGrid" />
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div>
                              <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {p.name}
                              </h3>
                              <span className="text-[11px] font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full inline-block mt-1">
                                {getCategoryLabel(p.categoryKey)} · {p.subtitle}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 min-h-[3.75rem]">
                            {p.description}
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(p.createRoute);
                            }}
                            className={`w-full sm:w-auto px-4 py-2 ${p.colorScheme.accent} text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2`}
                          >
                            <PlusOutlined className="text-xs" />
                            {t('marketplace.deploy') || 'Deploy'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {filteredCatalogProducts.map((p) => {
                    return (
                      <div
                        key={p.key}
                        className="flex flex-col sm:flex-row sm:items-stretch gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => navigate(p.createRoute)}
                          className={`flex sm:w-48 flex-shrink-0 h-32 sm:h-auto min-h-[8rem] items-stretch justify-center bg-gradient-to-br ${p.colorScheme.gradientFrom} ${p.colorScheme.gradientTo} border-b sm:border-b-0 sm:border-r ${p.colorScheme.borderColor} px-2 py-3 sm:px-3`}
                        >
                          <MarketplaceProductLogo product={p} variant="catalogListInner" />
                        </button>
                        <div className="flex-1 p-5 flex flex-col sm:flex-row sm:items-center gap-4 min-w-0">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{p.name}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {getCategoryLabel(p.categoryKey)} · {p.subtitle}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{p.description}</p>
                          </div>
                          <div className="flex sm:flex-col gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleSelectProduct(p.key)}
                              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            >
                              {t('marketplace.manageInstances') || 'My instances'}
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(p.createRoute)}
                              className={`px-4 py-2 ${p.colorScheme.accent} text-white rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2`}
                            >
                              <PlusOutlined className="text-xs" />
                              {t('marketplace.deploy') || 'Deploy'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            selectedProduct && (
              <>
                {/* Product header */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MarketplaceProductLogo product={selectedProduct} variant="pageHeader" />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {selectedProduct.name}
                          </h3>
                          <span className="text-[11px] font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                            {selectedProduct.subtitle}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {selectedProduct.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(selectedProduct.createRoute)}
                      className={`px-3 py-1.5 ${selectedProduct.colorScheme.accent} text-white rounded-md text-xs font-medium transition-colors flex items-center gap-1.5`}
                    >
                      <PlusOutlined className="text-[10px]" />
                      {t('marketplace.deploy') || 'Deploy'}
                    </button>
                  </div>

                  {/* Mini stats */}
                  {allServices.length > 0 && (
                    <div className="flex items-center gap-5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
                        <span className="text-xs font-bold text-gray-900 dark:text-white">{allServices.length}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">Running</span>
                        <span className="text-xs font-bold text-green-600 dark:text-green-400">{runningCount}</span>
                      </div>
                      {deployingCount > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">Deploying</span>
                          <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{deployingCount}</span>
                        </div>
                      )}
                      {stoppedCount > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">Stopped</span>
                          <span className="text-xs font-bold text-gray-600 dark:text-gray-400">{stoppedCount}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Search + Filter */}
                <div className="flex flex-col sm:flex-row gap-3 mb-5">
                  <div className="flex-1 relative">
                    <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder={t('marketplace.searchInstances', { name: selectedProduct.name }) || `Search ${selectedProduct.name} instances...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="all">{t('marketplace.allStatus') || 'All Status'}</option>
                    <option value="running">{t('marketplace.running') || 'Running'}</option>
                    <option value="stopped">{t('marketplace.stopped') || 'Stopped'}</option>
                    <option value="deploying">{t('marketplace.deploying') || 'Deploying'}</option>
                    <option value="error">{t('marketplace.error') || 'Error'}</option>
                  </select>
                </div>

                {/* Instances */}
                {isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                          </div>
                        </div>
                        <div className="h-16 bg-gray-100 dark:bg-gray-700/50 rounded mb-3" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                      </div>
                    ))}
                  </div>
                ) : filteredServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredServices.map((svc) => {
                      const statusCfg = STATUS_CONFIG[svc.status] || STATUS_CONFIG.stopped;
                      return (
                        <button
                          key={svc.id}
                          onClick={() => navigate(selectedProduct.detailRoute(svc.id))}
                          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all text-left w-full group"
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <MarketplaceProductLogo product={selectedProduct} variant="instanceCard" />
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                  {svc.name}
                                </h3>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{svc.instanceType}</span>
                              </div>
                            </div>
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${statusCfg.color}`}>
                              {statusCfg.icon} {statusCfg.label}
                            </span>
                          </div>

                          {/* Info */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-2.5">
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                <SwapOutlined /> Region
                              </div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{svc.region}</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-2.5">
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                <InfoCircleOutlined /> Port
                              </div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedProduct.port}</p>
                            </div>
                          </div>

                          {/* Badges */}
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {svc.badges.map((badge, i) => {
                              const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(badge);
                              return (
                                <span
                                  key={i}
                                  className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded ${
                                    isIp
                                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-mono'
                                      : `${selectedProduct.colorScheme.badgeBg} ${selectedProduct.colorScheme.badgeText}`
                                  }`}
                                >
                                  {badge}
                                </span>
                              );
                            })}
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Created {svc.createdAgo}</span>
                            <RightOutlined className="text-xs text-gray-400 group-hover:text-blue-500 transition-colors" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-dashed">
                    <MarketplaceProductLogo product={selectedProduct} variant="instanceEmpty" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {searchTerm || filterStatus !== 'all'
                        ? 'No instances match your filters'
                        : `No ${selectedProduct.name} instances yet`}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm mx-auto">
                      {searchTerm || filterStatus !== 'all'
                        ? 'Try adjusting your search or status filter.'
                        : selectedProduct.description}
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                      <button
                        onClick={() => navigate(selectedProduct.createRoute)}
                        className={`px-5 py-2.5 ${selectedProduct.colorScheme.accent} text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2`}
                      >
                        <PlusOutlined />
                        Deploy {selectedProduct.name}
                      </button>
                    )}
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>

      {/* Help Modal */}
      <SideModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={
          isBrowseMode || !selectedProduct
            ? t('marketplace.helpAboutMarketplace') || 'About Marketplace'
            : `${t('marketplace.helpAbout') || 'About'} ${selectedProduct.name}`
        }
      >
        {isBrowseMode || !selectedProduct ? (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <AppstoreOutlined className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('marketplace.title')}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('marketplace.subtitle')}</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{t('marketplace.description')}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t('marketplace.helpCategoriesTitle') || 'Categories'}
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                {MARKETPLACE_CATEGORIES.map((c) => (
                  <li key={c.key} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                    <span className="font-medium">{c.label}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      ({getProductsByCategory(c.key).length})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('marketplace.helpSidebarHint') ||
                'Use the sidebar to open a product and manage your deployed instances, or deploy a new service from the catalog.'}
            </p>
          </div>
        ) : (
          selectedProduct && (
            <div className="space-y-8">
              <div className={`bg-gradient-to-r ${selectedProduct.colorScheme.gradientFrom} ${selectedProduct.colorScheme.gradientTo} rounded-lg p-6 border ${selectedProduct.colorScheme.borderColor}`}>
                <div className="flex items-center gap-3 mb-3">
                  <MarketplaceProductLogo product={selectedProduct} variant="helpModal" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedProduct.name}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedProduct.subtitle}</p>
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{selectedProduct.description}</p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <span className={`w-8 h-8 ${selectedProduct.colorScheme.bg} rounded-full flex items-center justify-center ${selectedProduct.colorScheme.text} font-bold text-sm`}>1</span>
                  {selectedProduct.helpContent.whatIs}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed pl-10">
                  {selectedProduct.helpContent.whatIsDesc}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">2</span>
                  Common Use Cases
                </h3>
                <ul className="space-y-2 pl-10 text-gray-700 dark:text-gray-300">
                  {selectedProduct.helpContent.useCases.map((uc) => (
                    <li key={uc} className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 ${selectedProduct.colorScheme.accent.split(' ')[0]} rounded-full`} />
                      {uc}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 font-bold text-sm">3</span>
                  Included Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-10">
                  {selectedProduct.helpContent.features.map((f) => (
                    <div key={f} className="flex items-start gap-2">
                      <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
      </SideModal>
    </div>
  );
};

export default MarketplacePage;
